const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

export const PX_PER_MINUTE = 1.35

/**
 * @param {string} dateStr YYYY-MM-DD
 */
export function dayKeyFromDate(dateStr) {
  const date = new Date(`${dateStr}T12:00:00`)
  return DAY_KEYS[date.getDay()]
}

/**
 * @param {string} time HH:mm
 */
export function parseTimeToMinutes(time) {
  const [hours, minutes] = time.split(':').map((part) => Number(part))
  return hours * 60 + minutes
}

/**
 * @param {number} minutes
 */
export function formatMinutesLabel(minutes) {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`
}

/**
 * @param {number} minutes
 * @param {number} slotMinutes
 */
export function snapToSlotMinutes(minutes, slotMinutes) {
  if (slotMinutes <= 0) {
    return minutes
  }

  return Math.round(minutes / slotMinutes) * slotMinutes
}

/**
 * @param {number} openMinutes
 * @param {number} closeMinutes
 * @param {number} slotMinutes
 */
export function buildTimeSlotLabels(openMinutes, closeMinutes, slotMinutes) {
  const slots = []

  for (let cursor = openMinutes; cursor < closeMinutes; cursor += slotMinutes) {
    slots.push({
      minutes: cursor,
      label: formatMinutesLabel(cursor),
    })
  }

  return slots
}

/**
 * Visual grid hierarchy for the day calendar.
 * Hour = strong, half-hour = medium, quarter = faint.
 *
 * @param {number} openMinutes
 * @param {number} closeMinutes
 * @returns {Array<{ minutes: number, strength: 'hour' | 'half' | 'quarter', showLabel: boolean }>}
 */
export function buildCalendarGridLines(openMinutes, closeMinutes) {
  const lines = []

  for (let cursor = openMinutes; cursor <= closeMinutes; cursor += 15) {
    const isHour = cursor % 60 === 0
    const isHalf = cursor % 30 === 0

    lines.push({
      minutes: cursor,
      strength: isHour ? 'hour' : isHalf ? 'half' : 'quarter',
      showLabel: isHour || isHalf,
    })
  }

  return lines
}

/**
 * @param {number} bookedMinutes
 */
export function formatBookedDuration(bookedMinutes) {
  if (bookedMinutes <= 0) {
    return null
  }

  const hours = Math.floor(bookedMinutes / 60)
  const mins = bookedMinutes % 60

  if (hours === 0) {
    return `${mins}m booked`
  }

  if (mins === 0) {
    return `${hours}h booked`
  }

  return `${hours}h ${mins}m booked`
}

/**
 * @param {string} iso
 */
export function isoToMinutes(iso) {
  const date = new Date(iso)
  return date.getHours() * 60 + date.getMinutes()
}

/**
 * @param {string} startsAt
 * @param {string} endsAt
 * @param {number} dayStartMinutes
 * @param {number} pxPerMinute
 */
export function getAppointmentBlockStyle(startsAt, endsAt, dayStartMinutes, pxPerMinute) {
  const startMinutes = isoToMinutes(startsAt)
  const endMinutes = isoToMinutes(endsAt)
  const top = Math.max(0, (startMinutes - dayStartMinutes) * pxPerMinute)
  const height = Math.max((endMinutes - startMinutes) * pxPerMinute, 28)

  return { top, height }
}

/**
 * @param {number} offsetY
 * @param {number} dayStartMinutes
 * @param {number} pxPerMinute
 * @param {number} slotMinutes
 */
export function dropOffsetToMinutes(offsetY, dayStartMinutes, pxPerMinute, slotMinutes) {
  const rawMinutes = dayStartMinutes + offsetY / pxPerMinute
  return snapToSlotMinutes(rawMinutes, slotMinutes)
}

/**
 * @param {string} dayDate YYYY-MM-DD
 * @param {number} minutesFromMidnight
 */
export function minutesToIso(dayDate, minutesFromMidnight) {
  const hours = Math.floor(minutesFromMidnight / 60)
  const minutes = minutesFromMidnight % 60

  return `${dayDate}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
}

/**
 * @param {import('@/types/api').Appointment} appointment
 * @param {number | null | undefined} targetStylistId
 * @param {string} dayDate
 * @param {number} dropMinutes
 */
export function buildReschedulePayload(appointment, targetStylistId, dayDate, dropMinutes) {
  return {
    starts_at: minutesToIso(dayDate, dropMinutes),
    stylist_id: targetStylistId ?? appointment.stylist_id ?? null,
  }
}

/**
 * @param {string} status
 */
export function appointmentStatusTone(status) {
  switch (status) {
    case 'in_chair':
      return 'active'
    case 'checked_in':
      return 'checked'
    case 'confirmed':
      return 'confirmed'
    case 'scheduled':
      return 'pending'
    case 'completed':
      return 'completed'
    case 'cancelled':
      return 'cancelled'
    case 'no_show':
      return 'noshow'
    default:
      return 'scheduled'
  }
}
